import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, LICENSE_CATEGORIES, type LicenseCategory } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { Loader } from '../components/Loader';
import { ErrorView } from '../components/ErrorView';
import { PageHeader } from '../components/PageHeader';
import { formatDuration, modeLabel } from '../lib/format';
import { clearToken, isMiniApp } from '../lib/auth';
import { haptic } from '../lib/telegram';
import { IconTile, TabGlyph } from '../components/AppIcons';
import { isNativeApp } from '../lib/push';
import { getExplStyle, setExplStyle, type ExplStyle } from '../lib/settings';

export function Profile() {
  const me = useFetch(() => api.me(), []);
  const history = useFetch(() => api.myAttempts(), []);
  const links = useFetch(() => api.links(), []);
  const navigate = useNavigate();
  const inMiniApp = isMiniApp();
  const [savingCat, setSavingCat] = useState(false);
  const [explStyle, setExplStyleState] = useState<ExplStyle>(getExplStyle());

  function handleLogout() {
    clearToken();
    navigate('/login', { replace: true });
  }

  async function handleCategoryChange(cat: LicenseCategory) {
    if (savingCat) return;
    setSavingCat(true);
    haptic.selection();
    try {
      await api.updateMe({ licenseCategory: cat });
      me.reload();
    } finally {
      setSavingCat(false);
    }
  }

  return (
    <>
      <PageHeader title="Профіль" />
      <div className="space-y-4 p-4">
        {me.loading && <Loader />}
        {me.error && <ErrorView error={me.error} onRetry={me.reload} />}

        {me.data && (
          <>
            <section className="card flex items-center gap-3">
              {me.data.user.photoUrl ? (
                <img src={me.data.user.photoUrl} alt="" className="h-14 w-14 rounded-full object-cover" />
              ) : (
                <div className="grid h-14 w-14 place-items-center rounded-full bg-tg-bg text-tg-hint">
                  <TabGlyph kind="profile" className="h-7 w-7" />
                </div>
              )}
              <div>
                <div className="font-medium">
                  {me.data.user.firstName} {me.data.user.username && <span className="text-tg-hint">@{me.data.user.username}</span>}
                </div>
                <div className="text-xs text-tg-hint">
                  Рівень {me.data.user.level} · {me.data.user.xp} XP
                </div>
              </div>
            </section>

            {/* Прогрес-бар до наступного рівня */}
            <section className="card">
              <div className="mb-1 flex justify-between text-xs text-tg-hint">
                <span>Рівень {me.data.user.level}</span>
                <span>
                  {me.data.user.xp - me.data.progress.currentLevelXp} /{' '}
                  {me.data.progress.nextLevelXp - me.data.progress.currentLevelXp} XP до {me.data.user.level + 1}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-tg-bg">
                <div
                  className="h-full bg-tg-button transition-all"
                  style={{
                    width: `${
                      ((me.data.user.xp - me.data.progress.currentLevelXp) /
                        (me.data.progress.nextLevelXp - me.data.progress.currentLevelXp)) *
                      100
                    }%`,
                  }}
                />
              </div>
            </section>

            <section className="card space-y-2">
              <label className="block text-xs font-medium text-tg-hint">
                Категорія прав
              </label>
              <div className="flex flex-wrap gap-1.5">
                {LICENSE_CATEGORIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => handleCategoryChange(c)}
                    disabled={savingCat}
                    className={`min-w-12 rounded-lg px-3 py-1.5 text-sm font-medium ${
                      me.data!.user.licenseCategory === c
                        ? 'bg-tg-button text-tg-button-text'
                        : 'bg-tg-bg text-tg-text'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <p className="text-xs text-tg-hint">
                Питання, специфічні для інших категорій, будуть приховані.
              </p>
            </section>

            <section className="card space-y-2">
              <label className="block text-xs font-medium text-tg-hint">
                Стиль пояснень до питань
              </label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  ['instructor', '🎓 Інструктор', 'чітко, з пунктами ПДР'],
                  ['casual', '😎 Дружбан', 'просто, по-своєму'],
                ] as Array<[ExplStyle, string, string]>).map(([val, title, sub]) => (
                  <button
                    key={val}
                    onClick={() => {
                      haptic.selection();
                      setExplStyle(val);
                      setExplStyleState(val);
                    }}
                    className={`rounded-xl px-3 py-2.5 text-left ${
                      explStyle === val
                        ? 'bg-tg-button text-tg-button-text'
                        : 'bg-tg-bg text-tg-text'
                    }`}
                  >
                    <div className="text-sm font-semibold">{title}</div>
                    <div className={`text-[11px] ${explStyle === val ? 'opacity-80' : 'text-tg-hint'}`}>{sub}</div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-tg-hint">
                Як пояснювати, чому відповідь правильна — після кожного питання в тесті.
              </p>
            </section>

            <section className="card space-y-3">
              <h3 className="text-xs font-medium uppercase tracking-wide text-tg-hint">
                Загальний прогрес
              </h3>
              <ProgressLine
                label="Точність"
                value={me.data.stats.accuracy}
                color={me.data.stats.accuracy >= 80 ? 'success' : me.data.stats.accuracy >= 50 ? 'warning' : 'destructive'}
              />
              <div className="grid grid-cols-3 gap-2 pt-1 text-center">
                <Stat label="Запитань пройдено" value={me.data.stats.totalAnswers} />
                <Stat label="Правильних" value={me.data.stats.correctAnswers} accent="success" />
                <Stat label="Неправильних" value={me.data.stats.wrongAnswers} accent="destructive" />
              </div>
            </section>

            <section className="card space-y-3">
              <h3 className="text-xs font-medium uppercase tracking-wide text-tg-hint">
                Іспити
              </h3>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat
                  label="Здано"
                  value={`${me.data.stats.officialPassed}/${me.data.stats.officialAttempts}`}
                  accent="success"
                />
                <Stat
                  label="Середній бал"
                  value={`${me.data.stats.averageOfficialScore}/20`}
                />
                <Stat label="Днів поспіль" value={me.data.user.streakDays} />
              </div>
            </section>

            {/* Канал новин і підтримка — лінки з AppSetting (ті самі, що в боті) */}
            {/* Кнопка «Завантаж додаток» — у Telegram і на сайті (у самій апці зайва) */}
            {!isNativeApp() && (
              <a
                href="https://play.google.com/store/apps/details?id=online.pdrtest.app.twa"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => haptic.light()}
                className="card flex items-center gap-3 hover:bg-tg-bg/40"
              >
                <IconTile kind="flash" className="h-10 w-10" glyphClass="h-5 w-5" />
                <div className="flex-1">
                  <div className="font-medium">Завантаж наш додаток</div>
                  <div className="text-xs text-tg-hint">
                    Google Play · працює без інтернету · вхід тим самим акаунтом
                  </div>
                </div>
                <span className="text-tg-hint">›</span>
              </a>
            )}

            {links.data && (
              <section className="space-y-2">
                <a
                  href={links.data.newsChannelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card flex items-center gap-3 hover:bg-tg-bg/40"
                >
                  <IconTile kind="channel" className="h-10 w-10" glyphClass="h-5 w-5" />
                  <div className="flex-1">
                    <div className="font-medium">Наш канал</div>
                    <div className="text-xs text-tg-hint">Новини, тести тижня, розбори знаків</div>
                  </div>
                  <span className="text-tg-hint">›</span>
                </a>
                <a
                  href={links.data.supportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card flex items-center gap-3 hover:bg-tg-bg/40"
                >
                  <IconTile kind="support" className="h-10 w-10" glyphClass="h-5 w-5" />
                  <div className="flex-1">
                    <div className="font-medium">Підтримка</div>
                    <div className="text-xs text-tg-hint">Питання, проблеми, пропозиції — пиши</div>
                  </div>
                  <span className="text-tg-hint">›</span>
                </a>
              </section>
            )}

            {me.data.user.role === 'ADMIN' && (
              <Link to="/admin" className="card flex items-center gap-3 hover:bg-tg-bg/40">
                <IconTile kind="admin" className="h-10 w-10" glyphClass="h-5 w-5" />
                <div className="flex-1">
                  <div className="font-medium">Адмін-панель</div>
                  <div className="text-xs text-tg-hint">Управління питаннями і користувачами</div>
                </div>
                <span className="text-tg-hint">›</span>
              </Link>
            )}

            {/* Logout — лише для веб-версії. У Mini App вихід зайвий, бо auth = initData від Telegram */}
            {!inMiniApp && (
              <button onClick={handleLogout} className="btn-secondary w-full text-tg-destructive">
                Вийти
              </button>
            )}

            {me.data.achievements.length > 0 && (
              <section className="space-y-2">
                <h2 className="px-1 text-sm font-semibold uppercase tracking-wide text-tg-hint">
                  Досягнення ({me.data.achievements.length})
                </h2>
                <div className="grid grid-cols-2 gap-2">
                  {me.data.achievements.map((a) => (
                    <div key={a.slug} className="card">
                      <IconTile kind="medal" className="h-9 w-9 rounded-lg" glyphClass="h-5 w-5" />
                      <div className="mt-1.5 text-sm font-medium">{a.nameUk}</div>
                      <div className="text-xs text-tg-hint">{a.description}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        <section className="space-y-2">
          <h2 className="px-1 text-sm font-semibold uppercase tracking-wide text-tg-hint">
            Історія тестів
          </h2>
          {history.loading && <Loader />}
          {history.error && <ErrorView error={history.error} onRetry={history.reload} />}
          {history.data?.length === 0 && (
            <p className="card text-sm text-tg-hint">Поки що жодного тесту.</p>
          )}
          {history.data?.map((a) => (
            <div key={a.id} className="card flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">
                  {a.category?.nameUk ?? modeLabel(a.mode)}
                </div>
                <div className="text-xs text-tg-hint">
                  {new Date(a.finishedAt).toLocaleString('uk-UA', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {' · '}
                  {formatDuration(a.timeSpentMs)}
                </div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-semibold ${a.passed ? 'text-tg-success' : 'text-tg-destructive'}`}>
                  {a.score}/{a.total}
                </div>
                <div className="text-xs text-tg-hint">{a.passed ? 'Здано' : 'Не здано'}</div>
              </div>
            </div>
          ))}
        </section>
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: 'success' | 'destructive';
}) {
  const cls =
    accent === 'success' ? 'text-tg-success' : accent === 'destructive' ? 'text-tg-destructive' : '';
  return (
    <div>
      <div className={`text-xl font-semibold ${cls}`}>{value}</div>
      <div className="text-xs text-tg-hint">{label}</div>
    </div>
  );
}

function ProgressLine({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: 'success' | 'warning' | 'destructive';
}) {
  const bar =
    color === 'success' ? 'bg-tg-success' : color === 'destructive' ? 'bg-tg-destructive' : 'bg-yellow-500';
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs">
        <span className="text-tg-hint">{label}</span>
        <span className="font-medium">{value}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-tg-bg">
        <div className={`h-full ${bar} transition-all`} style={{ width: `${Math.min(100, value)}%` }} />
      </div>
    </div>
  );
}
